import {
    createFileRoute,
    Link,
    Outlet,
    useNavigate,
} from '@tanstack/react-router';

import { SettingsLoadingState } from '@apps/web/components/settings/settings_page_shell';
import { TeamMemberNotificationEditor } from '@apps/web/components/settings/team_member_notification_editor';
import { Button } from '@apps/web/components/ui/button';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';
import { isOrganizationAdmin } from '@apps/web/lib/organization_permissions';
import { PHOEBE_TEAM_USER_EMAIL } from '@apps/web/lib/phoebe_team_user';
import {
    useOrganizationUsers,
    useUserProfile,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

// Member identity and authorization stay outside the reusable settings pages.
export const Route = createFileRoute(
    '/_app/settings/team-members_/$memberUserId',
)({
    component: TeamMemberSettingsLayout,
});

function TeamMemberSettingsLayout() {
    const { memberUserId } = Route.useParams();
    const { organizationId, isLoading: isLoadingGates } = useSettingsGates();
    const { data: profile, isLoading: isLoadingProfile } = useUserProfile({
        reason: 'team-member-settings',
    });
    const canManage = isOrganizationAdmin(profile, organizationId);
    const {
        data: users,
        isLoading,
        error,
        mutate,
    } = useOrganizationUsers(canManage ? organizationId : null, {
        reason: 'team-member-settings',
    });
    const navigate = useNavigate();
    const members = users?.filter(
        (user) => user.id && user.email !== PHOEBE_TEAM_USER_EMAIL,
    );
    const member = members?.find((user) => user.id === memberUserId);
    const backLink = (
        <Link
            to="/settings/team-members"
            className="text-muted-foreground hover:text-foreground text-sm"
        >
            ← Team Members
        </Link>
    );

    if (isLoadingGates || isLoadingProfile) {
        return <SettingsLoadingState />;
    }
    if (!canManage) {
        return (
            <SettingsPageShell title="Team member settings" eyebrow={backLink}>
                <p role="alert">
                    Only organization admins can manage a teammate’s settings.
                </p>
            </SettingsPageShell>
        );
    }
    if (isLoading) {
        return <SettingsLoadingState />;
    }
    if (error && !users) {
        return (
            <SettingsPageShell title="Team member settings" eyebrow={backLink}>
                <div role="alert" className="space-y-3">
                    <p>Team members could not be loaded.</p>
                    <Button variant="outline" onClick={() => void mutate()}>
                        Try again
                    </Button>
                </div>
            </SettingsPageShell>
        );
    }
    if (!member || !organizationId) {
        return (
            <SettingsPageShell title="Team member not found" eyebrow={backLink}>
                <p>
                    This person is not available in the selected organization.
                </p>
            </SettingsPageShell>
        );
    }

    const name =
        [member.first_name, member.last_name].filter(Boolean).join(' ') ||
        member.email;
    return (
        <SettingsPageShell
            title={name}
            description={member.email}
            eyebrow={backLink}
        >
            <div className="space-y-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div className="w-full space-y-2 sm:max-w-xs">
                        <Label htmlFor="settings-team-member">
                            Team member
                        </Label>
                        <Select
                            value={memberUserId}
                            onValueChange={(next) =>
                                void navigate({
                                    to: '/settings/team-members/$memberUserId/notifications',
                                    params: { memberUserId: next },
                                })
                            }
                        >
                            <SelectTrigger id="settings-team-member">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {members?.map(
                                    (candidate) =>
                                        candidate.id && (
                                            <SelectItem
                                                key={candidate.id}
                                                value={candidate.id}
                                            >
                                                {[
                                                    candidate.first_name,
                                                    candidate.last_name,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' ') ||
                                                    candidate.email}
                                            </SelectItem>
                                        ),
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                    <p className="text-muted-foreground text-sm">
                        Review notification changes before saving for {name}.
                    </p>
                </div>
                <nav
                    aria-label="Team member settings"
                    className="border-b pb-3"
                >
                    <Link
                        to="/settings/team-members/$memberUserId/notifications"
                        params={{ memberUserId }}
                        className="text-sm font-medium"
                    >
                        Notifications
                    </Link>
                </nav>
                {/* Each membership owns a separate notification draft. */}
                <div key={`${organizationId}:${memberUserId}`}>
                    <TeamMemberNotificationEditor
                        organizationId={organizationId}
                        memberUserId={memberUserId}
                    >
                        <Outlet />
                    </TeamMemberNotificationEditor>
                </div>
            </div>
        </SettingsPageShell>
    );
}
